import { Button, MultiSelect, Textarea } from '@mantine/core'
import { Modal } from 'react-bootstrap'
import Select2 from '../../components/select2'
import InputErrorBox from '../../components/InputErrorBox'
import CustomFileInput from '../../components/CustomInput/CustomFileInput'
import { setupAxios } from '../../modules/auth'
import { API_BASE_URL } from '../../../utils/contants'

interface LeaveDate {
  startDate: string
  endDate: string
  type: string
  startTime?: string
  endTime?: string
}

interface LeaveTypeOption {
  label: string
  value: string
  disabled?: boolean
}

interface UserNameOption {
  label: string
  value: string
  email?: string
  disabled?: boolean
}

interface MyLeaveModalProps {
  open: boolean
  onClose: () => void
  inputField: any
  setInputField: (fn: (prev: any) => any) => void
  leaveType: LeaveTypeOption[]
  sortedUserNames: UserNameOption[]
  inputError: any
  setInputError: (err: any) => void
  dateError: string
  setDateError: (err: string) => void
  handleStartDateChange?: (e: any, index: number) => void // legacy; now handled inline
  handleEndDateChange?: (e: any, index: number) => void   // legacy; now handled inline
  handleTypeChange: (e: any, index: number) => void
  removeLeaveDate: (index: number) => void
  addLeaveDate: () => void
  getMinStartDate: () => string
  getMaxEndDate: () => string
  isDateDisabled: (dateStr: string) => boolean
  leaveReasons: any[]
  reasonMetaList: any[]
  selectedReasonMeta: { isEmergency: boolean } | null
  setSelectedReasonMeta: (meta: any) => void
  SubmitAPIStatus: any
  onSubmit: () => void
  onTrash: () => void
  themeStyles: any
  currentUser: any
  currentUserFull: any
  dropdownOptions: UserNameOption[]
}

// ===== Earned Leave Reasons (Static) =====
const earnedLeaveReasons = [
  { label: 'Medical Leave', value: 'earned_medical', isEmergency: false },
  { label: 'Personal Leave', value: 'earned_personal', isEmergency: false },
  { label: 'Emergency Leave', value: 'earned_emergency', isEmergency: true },
];


const MyLeaveModal = ({
  open,
  onClose,
  inputField,
  setInputField,
  leaveType,
  sortedUserNames,
  inputError,
  setInputError,
  dateError,
  setDateError,
  handleTypeChange,
  removeLeaveDate,
  addLeaveDate,
  getMinStartDate,
  getMaxEndDate,
  isDateDisabled,
  leaveReasons,
  reasonMetaList,
  selectedReasonMeta,
  setSelectedReasonMeta,
  SubmitAPIStatus,
  onSubmit,
  onTrash,
  themeStyles,
  currentUser,
  currentUserFull,
  dropdownOptions,

}: MyLeaveModalProps) => {



  // Function to generate leave description template
  const generateLeaveTemplate = (startDate: string, endDate: string, employeeName: string) => {
    const formatDate = (dateStr: string) => {
      if (!dateStr) return 'YYYY/MM/DD';
      const date = new Date(dateStr);
      return date.toISOString().split('T')[0].replace(/-/g, '/');
    };

    const startFormatted = formatDate(startDate);
    const endFormatted = formatDate(endDate);

    // Calculate resume date (next working day after end date)
    let resumeDate = 'YYYY/MM/DD (date)';
    if (endDate) {
      const endDateObj = new Date(endDate);
      endDateObj.setDate(endDateObj.getDate() + 1);
      resumeDate = endDateObj.toISOString().split('T')[0].replace(/-/g, '/');
    }

    return `Dear Team,

I am on leave from ${startFormatted} to ${endFormatted}, will resume work on ${resumeDate}.

For any emergency my contact no. is _________. My work is deputed to Mr./Miss._______ until I return.

Thanks & Regards,
${employeeName || 'Name of Employee'}
Signature`;
  };

  const getJoinDate = () => {
    if (!currentUser?.joiningDate) return null;
    return new Date(currentUser.joiningDate);
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const getRestrictedLeaveTypes = (): string[] => {
    const joinDate = getJoinDate();
    if (!joinDate) return [];

    const oneMonthAfterJoining = new Date(joinDate);
    oneMonthAfterJoining.setMonth(oneMonthAfterJoining.getMonth() + 1);

    const restrictedTypes: string[] = [];

    if (today < oneMonthAfterJoining) {
      // Find casual leave types that should be restricted
      const casualLeaveTypes = leaveType.filter(lt =>
        lt.label && lt.label.toLowerCase().includes('casual')
      );
      restrictedTypes.push(...casualLeaveTypes.map(lt => lt.value));
    }

    return restrictedTypes;
  }


  // Filter leave types based on eligibility
  const getAvailableLeaveTypes = () => {
    const restrictedTypes = getRestrictedLeaveTypes();
    return leaveType.map(lt => ({
      ...lt,
      disabled: restrictedTypes.includes(lt.value),
      label: restrictedTypes.includes(lt.value)
        ? `${lt.label} (Available after ${getJoinDate() ? new Date(new Date(getJoinDate()!).setMonth(new Date(getJoinDate()!).getMonth() + 1)).toDateString() : 'one month'})`
        : lt.label
    }));
  }

  // Reset modal state when leave type changes
  const resetModalState = (newLeaveType: string) => {
    setInputField((prev: any) => ({
      ...prev,
      leaveType: newLeaveType,
      // Reset leave-specific fields
      reasonId: '',
      reason: '',
      description: '',
      attachment: null,
      isAttachmentRequired: false,
      // Reset date fields
      leaveDate: [{
        startDate: '',
        endDate: '',
        type: 'full',
      }],
    }));

    // Reset related state
    setSelectedReasonMeta(null);
    setInputError({});
    setDateError('');
  };

  const rmEmail = inputField.recipients?.to;
  const dataWithDisabled = sortedUserNames.map(option => ({
    ...option,
    disabled: option.value === rmEmail, // Disable reporting manager since they're already in "To" field
  }));
  const handleCCChange = (selectedOptions: string[]) => {
    // Don't automatically include reporting manager in CC - let user choose
    setInputField((prevInputField: any) => ({
      ...prevInputField,
      recipients: {
        ...prevInputField.recipients,
        cc: selectedOptions,
      },
    }))
  }

  // Date field handlers
  const handleStartDateChange = async (e, index) => {
    const selectedDateStr = e.target.value;
    const selectedStartDate = new Date(selectedDateStr);
    let hasError = false;

    // Immediately reflect value in UI before validations so the date appears
    setInputField((prevInputField: any) => {
      const updatedLeaveDate = [...prevInputField.leaveDate];
      const currentLeave = updatedLeaveDate[index];
      currentLeave.startDate = selectedDateStr;
      // If Short Leave, auto-set end date to the same day
      const selectedLeaveType = leaveType.find(lt => lt.value === prevInputField.leaveType);
      const isShortLeave = selectedLeaveType?.label?.toLowerCase().includes('short') || false;
      if (isShortLeave) {
        currentLeave.endDate = selectedDateStr;
      }
      // If no end date yet, keep type untouched; otherwise keep logic later
      return {
        ...prevInputField,
        leaveDate: updatedLeaveDate,
      };
    });

    // Check if the selected date is disabled (weekend or holiday)
    if (isDateDisabled(selectedDateStr)) {
      const date = new Date(selectedDateStr);
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      const isWeekend = ['Saturday', 'Sunday'].includes(dayName);
      const errorMessage = isWeekend
        ? `${dayName} is a weekend. Please select a working day.`
        : 'Selected date is a holiday. Please select a different date.';

      setInputError((prev) => ({
        ...prev,
        [`startDate${index}`]: errorMessage,
      }));
      hasError = true;
    }

    const joinDate = getJoinDate();

    // Find leave type and reason
    const selectedLeaveType = leaveType.find(
      (lt) => lt.value === inputField.leaveType
    );
    const leaveTypeName = selectedLeaveType?.label?.toLowerCase() || '';

    // Check if emergency reason is selected (based on selected reason, not leave type)
    const isEmergencyReason = selectedReasonMeta?.isEmergency || false;

    // CASUAL LEAVE VALIDATIONS
    if (leaveTypeName.includes('casual')) {
      // New joinee restriction for casual leave
      if (joinDate) {
        const oneMonthAfterJoining = new Date(joinDate);
        oneMonthAfterJoining.setMonth(oneMonthAfterJoining.getMonth() + 1);

        if (selectedStartDate < oneMonthAfterJoining) {
          setInputError((prev) => ({
            ...prev,
            [`startDate${index}`]: `Casual Leave cannot be availed during the first month after joining. You can apply from ${oneMonthAfterJoining.toDateString()}.`,
          }));
          hasError = true;
        }
      }

      // Different validation for Emergency vs Regular Casual Leave
      if (isEmergencyReason) {
        // Emergency Leave: Allow backdated applications. Still restrict far-future selection.
        const today = new Date();
        // Limit future selection to within the next 12 hours
        const maxAdvanceTime = new Date(today);
        maxAdvanceTime.setHours(maxAdvanceTime.getHours() + 12); // 12 hours from now

        if (selectedStartDate > maxAdvanceTime) {
          setInputError(prev => ({
            ...prev,
            [`startDate${index}`]: 'Emergency leave can only be applied up to 12 hours (0.5 day) in advance. For advance planning, use regular casual leave.',
          }));
          hasError = true;
        }
      } else {
        // Regular Casual Leave: Must apply in advance per current policy
        try {
          const selectedLeaveType = leaveType.find(lt => lt.value === inputField.leaveType);
          const clTypeId = selectedLeaveType?.value || '';
          if (clTypeId) {
            const { token, tenantId } = setupAxios();
            const res = await fetch(`${API_BASE_URL}/leave-policies?leaveTypeId=${clTypeId}`, {
              headers: { Authorization: `Bearer ${token}`, 'X-tenant-Id': `${tenantId}` },
            });
            const policyJson = await res.json();
            const policy = Array.isArray(policyJson?.data) ? policyJson.data.find((p: any) => p.leaveTypeId === clTypeId) : policyJson?.data;
            const minHours = Number(policy?.minAdvanceNotice) || 0;
            if (minHours > 0) {
              const minAdvanceTime = new Date(today);
              minAdvanceTime.setHours(minAdvanceTime.getHours() + minHours);
              if (selectedStartDate < minAdvanceTime) {
                setInputError(prev => ({
                  ...prev,
                  [`startDate${index}`]: `Casual Leave must be applied at least ${minHours} hours in advance.`,
                }));
                hasError = true;
              }
            }
          }
        } catch (_) { }
      }
    }

    // EARNED LEAVE VALIDATIONS
    else if (leaveTypeName.includes('earned')) {
      // Must have ≥ 1 year service
      if (joinDate) {
        const oneYearAfterJoining = new Date(joinDate);
        oneYearAfterJoining.setFullYear(oneYearAfterJoining.getFullYear() + 1);

        if (today < oneYearAfterJoining) {
          setInputError((prev) => ({
            ...prev,
            [`startDate${index}`]: 'Earned Leave can be applied only after 1 year of service.',
          }));
          return;
        }
      }

      // Must apply at least 7 days in advance
      const minAdvanceTime = new Date(today);
      minAdvanceTime.setDate(minAdvanceTime.getDate() + 7);

      if (selectedStartDate < minAdvanceTime) {
        setInputError(prev => ({
          ...prev,
          [`startDate${index}`]: 'Earned Leave must be applied minimum 7 days in advance.',
        }));
        return;
      }
    }

    // COMP-OFF VALIDATIONS
    else if (leaveTypeName.includes('comp') || leaveTypeName.includes('compensatory')) {
      // Must be applied within 1 month of work date
      const maxAllowedDate = new Date(today);
      maxAllowedDate.setMonth(maxAllowedDate.getMonth() + 1);

      if (selectedStartDate > maxAllowedDate) {
        setInputError(prev => ({
          ...prev,
          [`startDate${index}`]: 'Comp-Off must be used within 1 month of work date.',
        }));
        return;
      }
    }

    // SHORT LEAVE VALIDATIONS
    else if (leaveTypeName.includes('short')) {
      // Note: Monthly limit validation will be handled in backend/form validation
      // Duration validation will be handled in handleEndDateChange
    }

    // LEAVE WITHOUT PAY (LWP) VALIDATIONS  
    else if (leaveTypeName.includes('without pay') || leaveTypeName.includes('lwp')) {
      // Note: LWP validations are mostly policy-based and handled in backend
    }



    // Clear error if no validation issues
    if (!hasError) {
      setInputError((prev) => ({
        ...prev,
        [`startDate${index}`]: '',
      }));
    }

    // After value reflected, adjust type based on date comparison
    setInputField((prevInputField: any) => {
      const updatedLeaveDate = [...prevInputField.leaveDate];
      const currentLeave = updatedLeaveDate[index];
      if (selectedDateStr === currentLeave.endDate) {
        currentLeave.type = currentLeave.type || 'FULL';
        if (!currentLeave.type || currentLeave.type === '') currentLeave.type = 'FULL';
      } else {
        currentLeave.type = 'FULL';
      }
      return {
        ...prevInputField,
        leaveDate: updatedLeaveDate,
        isAttachmentRequired: false,
      };
    });
  }

  const handleEndDateChange = async (e, index) => {
    const selectedDateStr = e.target.value;
    const selectedEnd = new Date(selectedDateStr);
    const selectedStart = new Date(inputField.leaveDate[index].startDate);
    // Immediately reflect value in UI so the date appears even if validation fails later
    setInputField((prevInputField: any) => {
      const updatedLeaveDate = [...prevInputField.leaveDate];
      const currentLeave = updatedLeaveDate[index];
      currentLeave.endDate = selectedDateStr;
      return {
        ...prevInputField,
        leaveDate: updatedLeaveDate,
      };
    });

    // Check if the selected date is disabled (weekend or holiday)
    if (isDateDisabled(selectedDateStr)) {
      const date = new Date(selectedDateStr);
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
      const isWeekend = ['Saturday', 'Sunday'].includes(dayName);
      const errorMessage = isWeekend
        ? `${dayName} is a weekend. Please select a working day.`
        : 'Selected date is a holiday. Please select a different date.';

      setInputError((prev) => ({
        ...prev,
        [`endDate${index}`]: errorMessage,
      }));
      return;
    }

    // Find leave type and reason
    const selectedLeaveType = leaveType.find(
      (lt) => lt.value === inputField.leaveType
    );
    const leaveTypeName = selectedLeaveType?.label?.toLowerCase() || '';
    const isEmergencyReason = selectedReasonMeta?.isEmergency || false;

    // NOTE: Financial year restriction removed - users can apply for leave on any future date
    // Leave balance deductions will be handled appropriately when the leave is approved

    // Calculate duration in days and hours
    const diffInTime = selectedEnd.getTime() - selectedStart.getTime();
    let diffInDays = diffInTime / (1000 * 3600 * 24);
    const diffInHours = diffInTime / (1000 * 3600);

    // Adjust for same date calculations
    if (diffInDays === 0) {
      // Same start and end date
      const leaveTypeSelection = inputField.leaveDate[index].type;
      const selectedLeaveTypeName = leaveType.find(lt => lt.value === inputField.leaveType)?.label?.toLowerCase() || '';

      // For Short Leave, same date with half/full day is not appropriate
      if (selectedLeaveTypeName.includes('short')) {
        // Short Leave should specify actual start and end times, not use half/full day
        // For now, we'll prevent this configuration in validation above
        diffInDays = 0; // Will be caught in validation
      } else {
        // For other leave types, use normal same-date logic
        if (leaveTypeSelection === 'FULL') {
          diffInDays = 1; // Full day
        } else if (leaveTypeSelection === 'FIRST_HALF' || leaveTypeSelection === 'SECOND_HALF') {
          diffInDays = 0.5; // Half day
        } else {
          // Default to full day if no type is specified for same date
          diffInDays = 1;
        }
      }
    } else {
      // For different dates, add 1 to include both start and end dates
      diffInDays = diffInDays + 1;
    }

    // Generic policy-based maxDaysPerRequest enforcement (applies to all types)
    try {
      const selectedType = leaveType.find(lt => lt.value === inputField.leaveType);
      const typeId = selectedType?.value || '';
      if (typeId) {
        const { token, tenantId } = setupAxios();
        const res = await fetch(`${API_BASE_URL}/leave-policies?leaveTypeId=${typeId}`, {
          headers: { Authorization: `Bearer ${token}`, 'X-tenant-Id': `${tenantId}` },
        });
        const policyJson = await res.json();
        const policy = Array.isArray(policyJson?.data) ? policyJson.data.find((p: any) => p.leaveTypeId === typeId) : policyJson?.data;
        const maxPer = Number(policy?.maxDaysPerRequest);
        if (!Number.isNaN(maxPer) && maxPer > 0) {
          if (diffInDays > maxPer) {
            setInputError((prev) => ({
              ...prev,
              [`endDate${index}`]: `Leave cannot exceed ${maxPer} day(s) per request as per policy.`,
            }));
            return;
          }
        }
      }
    } catch (_) { }

    // CASUAL LEAVE VALIDATIONS
    if (leaveTypeName.includes('casual')) {
      // Validation: Must be ≥ 0.5 day
      if (diffInDays < 0.5) {
        setInputError((prev) => ({
          ...prev,
          [`endDate${index}`]: 'Minimum Casual Leave must be 0.5 day.',
        }));
        return;
      }
    }

    // EARNED LEAVE VALIDATIONS
    else if (leaveTypeName.includes('earned')) {
      // Validation: Cannot be less than 1 day
      if (diffInDays < 1) {
        setInputError((prev) => ({
          ...prev,
          [`endDate${index}`]: 'Earned Leave must be at least 1 day.',
        }));
        return;
      }

      // Note: Balance validation (max 14) handled in backend
      // Note: 6 days continuous requirement handled as business rule
    }

    // SHORT LEAVE VALIDATIONS
    else if (leaveTypeName.includes('short')) {
      // Short Leave must be same day only
      if (selectedEnd.toDateString() !== selectedStart.toDateString()) {
        setInputError((prev) => ({
          ...prev,
          [`endDate${index}`]: 'Short Leave must be within the same day. Please select the same start and end date.',
        }));
        return;
      }
      // Force type to half or full selection handling stays; allow selection when same day
    }

    // COMP-OFF VALIDATIONS
    else if (leaveTypeName.includes('comp') || leaveTypeName.includes('compensatory')) {
      // Note: Most comp-off validations are policy-based
      // 6-hour minimum work validation handled in backend
    }

    // LEAVE WITHOUT PAY (LWP) VALIDATIONS
    else if (leaveTypeName.includes('without pay') || leaveTypeName.includes('lwp')) {
      // Note: LWP validations are mostly policy-based and handled in backend
      // Auto-application when other leaves exhausted is handled in backend
    }

    // Clear any previous errors
    setInputError((prev) => ({
      ...prev,
      [`endDate${index}`]: '',
    }));
    setDateError('');

    // Update the leave date and handle type field based on date comparison
    setInputField((prevInputField: any) => {
      const updatedLeaveDate = [
        ...prevInputField.leaveDate.slice(0, index),
        {
          ...prevInputField.leaveDate[index],
          endDate: e.target.value,
          // Auto-set type field based on date comparison
          type: e.target.value === prevInputField.leaveDate[index].startDate
            ? (prevInputField.leaveDate[index].type && prevInputField.leaveDate[index].type !== ''
              ? prevInputField.leaveDate[index].type
              : 'FULL') // Always default to FULL for same date if no type or empty type
            : 'FULL' // Set to FULL for different dates (multi-day leave)
        },
        ...prevInputField.leaveDate.slice(index + 1),
      ];

      return {
        ...prevInputField,
        leaveDate: updatedLeaveDate,
        isAttachmentRequired: false, // Attachment is optional at submission, required before approval
      };
    });
  }

  // Render
  return (
    <Modal
      show={open}
      className='modal-lg'
      onHide={onClose}
      animation={false}
      backdrop='static'
      centered
    >
      <div className='modal-header'>
        <h2 className='fw-bolder'>Apply Leave</h2>
        <div
          className='btn btn-icon btn-sm btn-active-icon-danger'
          style={{ cursor: 'pointer' }}
          onClick={onClose}
        >
          <i className='fa-solid fa-xmark fs-1' />
        </div>
      </div>
      <div className='card-body p-0'>
        <div className='d-block'>
          <div className='d-flex align-items-center border-bottom ps-8 pe-5 min-h-50px d-flex'>
            <div className='text-dark fw-bold w-35px'>To:</div>
            {inputField.recipients.to ? (
              <div className='d-flex align-items-center' style={{ width: '100%' }}>
                <div className='text-dark fw-bold'>
                  {/* {(() => {
                    // First try to find in sortedUserNames
                    const toUser = sortedUserNames.find(u => u.value === inputField.recipients.to);
                    if (toUser?.label) {
                      return toUser.label;
                    }
                    
                    // Fallback: Get name from currentUserFull.associates (reporting manager)
                    if (currentUserFull?.associates) {
                      const rmAssociate = currentUserFull.associates.find((a: any) => 
                        a.associateType === "RM" || a.associateType === "REPORTING_MANAGER"
                      );
                      if (rmAssociate?.userAssociate) {
                        const firstName = rmAssociate.userAssociate.firstName || '';
                        const lastName = rmAssociate.userAssociate.lastName || '';
                        return `${firstName} ${lastName}`.trim();
                      }
                    }
                    
                    return 'Reporting Manager';
                  })()} */}
                  {(() => {
                    if (currentUserFull?.associates) {
                      const rm = currentUserFull.associates.find((a: any) =>
                        a.associateType === 'RM' || a.associateType === 'REPORTING_MANAGER'
                      )?.userAssociate;

                      const hr = currentUserFull.associates.find((a: any) =>
                        a.associateType === 'HR' || a.associateType === 'HR_MANAGER'
                      )?.userAssociate;

                      const names = [rm, hr]
                        .filter(Boolean)
                        .map((u: any) => `${u.firstName || ''} ${u.lastName || ''}`.trim())
                        .join(' & ');

                      return names || 'Reporting Manager & HR';
                    }

                    return 'Reporting Manager & HR';
                  })()}

                </div>
                <div className='ms-3 text-muted small'>
                  (Reporting Manager & HR Manager- automatically selected)
                </div>
              </div>
            ) : (
              <div className='text-danger'>
                Please select at least one recipient.
              </div>
            )}
            {/* Show error for To field */}
            {inputError?.to && (
              <div className='ms-7 text-danger' style={{ fontSize: '13px' }}>{inputError.to}</div>
            )}
          </div>
          <div className='ms-7'>
            <InputErrorBox Message={inputError?.to} />
          </div>

          {/* <div className='align-items-center border-bottom ps-8 pe-5 min-h-50px d-flex'> */}
          {/* <div className='text-dark fw-bold w-35px'>Cc:</div> */}
          {/* <div className='d-flex align-items-center' style={{ width: '100%' }}>
              <MultiSelect
                data={dataWithDisabled}
                value={inputField.recipients.cc || []}
                onChange={handleCCChange}
                placeholder="Select additional recipients to CC (optional)..."
                searchable
                clearable
                maxDropdownHeight={200}
                styles={{
                  input: { fontSize: '14px' },
                  dropdown: { fontSize: '14px' },
                }}
                style={{ width: '100%' }}
              />
            </div> */}
          {/* Show error for Cc field if any */}
          {/* {inputError?.cc && (
              <div className='ms-7 text-danger' style={{ fontSize: '13px' }}>{inputError.cc}</div>
            )}
          </div> */}
          <div className='align-items-center border-bottom ps-8 pe-5 min-h-50px d-flex'>
            <div className='text-dark fw-bold w-35px'>Cc:</div>

            <div className='d-flex align-items-center' style={{ width: '100%' }}>
              <MultiSelect
                // data={sortedUserNames}
                // value={inputField.recipients.cc}
                // onChange={(values) =>
                //   setInputField(prev => ({
                //     ...prev,
                //     recipients: {
                //       ...prev.recipients,
                //       cc: values,
                //     },
                //   }))
                // }
                data={sortedUserNames}
                value={inputField.recipients.cc}
                onChange={handleCCChange}
                placeholder="Select additional recipients to CC (optional)..."
                searchable
                clearable
              />

            </div>

            {inputError?.cc && (
              <div className='ms-7 text-danger' style={{ fontSize: '13px' }}>
                {inputError.cc}
              </div>
            )}
          </div>


          <div className='border-bottom px-4'>
            <Select2
              data={getAvailableLeaveTypes()}
              value={inputField.leaveType}
              styles={(theme) => ({
                ...theme,
                ...themeStyles,
                '&[data-focus]': {
                  border: '1px solid red',
                },
              })}
              onChange={(selectedOption: any) => {
                // Check if the selected option is restricted
                const restrictedTypes = getRestrictedLeaveTypes();
                if (restrictedTypes.includes(selectedOption as string)) {
                  const joinDate = getJoinDate();
                  const oneMonthAfter = joinDate ? new Date(new Date(joinDate).setMonth(joinDate.getMonth() + 1)) : null;
                  setInputError((prev: any) => ({
                    ...prev,
                    leaveType: `Casual Leave cannot be availed during the first month after joining. Available from ${oneMonthAfter?.toDateString() || 'one month after joining'}.`
                  }));
                  return;
                }

                // Reset modal state with new leave type
                resetModalState(selectedOption);
              }}
            />
            {/* Show error for leaveType field */}
            {inputError?.leaveType && (
              <div className='ms-7 text-danger' style={{ fontSize: '13px' }}>{inputError.leaveType}</div>
            )}
          </div>

          {/* New Leave Reason Type Dropdown - MOVED ABOVE DATES */}
          {/* Leave Reason Dropdown (Casual + Earned) */}
          {(() => {
            const selectedLeaveType = leaveType.find(lt => lt.value === inputField.leaveType);
            const name = selectedLeaveType?.label?.toLowerCase() || '';

            const isCasual = name.includes('casual');
            const isEarned = name.includes('earned');

            if (!isCasual && !isEarned) return null;

            const dropdownData = isCasual ? leaveReasons : earnedLeaveReasons;

            return (
              <div className='border-bottom px-8 py-4'>
                <label className='form-label required'>Leave Reason</label>

                <Select2
                  data={dropdownData}
                  value={inputField.reasonId}
                  onChange={(selectedValue) => {
                    const selected = dropdownData.find(r => r.value === selectedValue);

                    setInputField(prev => ({
                      ...prev,
                      reasonId: selected?.value || '',
                      reason: selected?.label || '',
                    }));

                    setSelectedReasonMeta(selected?.isEmergency ? { isEmergency: true } : null);

                    if (selected?.isEmergency) {
                      setInputField(prev => ({ ...prev, isAttachmentRequired: true }));
                    } else {
                      setInputField(prev => ({ ...prev, isAttachmentRequired: false, attachment: null }));
                    }
                  }}
                  placeholder={
                    isCasual
                      ? "Select a reason for casual leave..."
                      : "Select a reason for earned leave..."
                  }
                  styles={{ item: { fontSize: '14px', lineHeight: '1.5rem' } }}
                />

                {inputError?.reasonId && (
                  <div className='text-danger' style={{ fontSize: '13px', marginTop: '5px' }}>
                    {inputError.reasonId}
                  </div>
                )}

                {selectedReasonMeta?.isEmergency && (
                  <div className='alert alert-warning mt-2 p-3' style={{ fontSize: '12px' }}>
                    <strong>⚡ Emergency Leave:</strong> Supporting documents are required.
                  </div>
                )}
              </div>
            );
          })()}

          {inputField.leaveDate.length === 1 && (
            <div className='ms-7'>
              <InputErrorBox Message={inputError?.leaveType} />
            </div>
          )}
          {inputField.leaveDate.map((leave: LeaveDate, index: number) => (
            <div key={index} className='d-flex mt-2 flex-wrap border-bottom'>
              <div className='d-flex mt-2 flex-wrap border-bottom '>
                <div className='fv-row me-4 ms-7'>
                  <label className=' required fs-6  fw-semibold mb-2'>Start Date</label>
                  <div className='col-12'>
                    <input
                      style={{ width: '190px' }}
                      className='form-control w-170px'
                      name='startDate'
                      type='date'
                      value={leave.startDate}
                      min={getMinStartDate()}
                      onChange={(e) => handleStartDateChange(e, index)}
                    />
                  </div>
                  <div style={{ width: '190px' }}>
                    <InputErrorBox Message={inputError[`startDate${index}`]} />
                    
                    {selectedReasonMeta?.isEmergency && (
                      <div
                        style={{
                          color: '#f5c542',
                          fontSize: '12px',
                          marginTop: '4px',
                          width: '190px',
                        }}
                      >
                        Emergency Leave can only be applied within the previous 1 month from today.
                      </div>
                    )}
                    
                  </div>
                </div>
                <div className='fv-row me-4 mb-2'>
                  <label className=' required fs-6 fw-semibold mb-2'>End Date</label>
                  <div className='col-12'>
                    <input
                      style={{ width: '190px' }}
                      className='form-control w-170px'
                      name='endDate'
                      type='date'
                      value={leave.endDate}
                      min={leave.startDate}
                      onChange={(e) => handleEndDateChange(e, index)}
                    />
                    <div style={{ width: '190px' }}>
                      <InputErrorBox Message={inputError[`endDate${index}`]} />
                    </div>
                    {dateError && <div className='text-danger'>{dateError}</div>}
                  </div>
                </div>
                <div>
                  {leave.startDate === leave.endDate && leave.startDate !== '' && (
                    <div className='fv-row me-1 mb-2 '>
                      <label className='required fw-bold fs-6 mb-2'>Type</label>
                      <div className='d-flex mt-3'>
                        {/* Hide Full option for Short Leave - only show First Half and Second Half */}
                        {(() => {
                          const selectedLeaveType = leaveType.find(lt => lt.value === inputField.leaveType);
                          const isShortLeave = selectedLeaveType?.label?.toLowerCase().includes('short') || false;
                          return !isShortLeave;
                        })() && (
                            <div className='form-check mb-2'>
                              <input
                                className='form-check-input'
                                type='radio'
                                id={`full-${index}`}
                                name={`type-${index}`}
                                value='FULL'
                                checked={leave.type === 'FULL'}
                                onChange={(e) => handleTypeChange(e, index)}
                              />
                              <label htmlFor={`full-${index}`} className='fw-bold text-gray-800 d-block me-3'>
                                Full
                              </label>
                            </div>
                          )}
                        <div className='form-check mb-2'>
                          <input
                            className='form-check-input'
                            type='radio'
                            id={`firstHalf-${index}`}
                            name={`type-${index}`}
                            value='FIRST_HALF'
                            checked={leave.type === 'FIRST_HALF'}
                            onChange={(e) => handleTypeChange(e, index)}
                          />
                          <label htmlFor={`firstHalf-${index}`} className='fw-bold text-gray-800 d-block me-3'>
                            First Half
                          </label>
                        </div>
                        <div className='form-check mb-2'>
                          <input
                            className='form-check-input'
                            type='radio'
                            id={`secondHalf-${index}`}
                            name={`type-${index}`}
                            value='SECOND_HALF'
                            checked={leave.type === 'SECOND_HALF'}
                            onChange={(e) => handleTypeChange(e, index)}
                          />
                          <label htmlFor={`secondHalf-${index}`} className='fw-bold text-gray-800 d-block'>
                            Second Half
                          </label>
                        </div>
                      </div>
                      <div className='mt-3'>
                        <InputErrorBox Message={inputError[`type${index}`]} />
                      </div>
                    </div>
                  )}
                </div>
                <div className='fv-row mb-2 mt-3'>
                  {index === 0 && <label className='  fs-6 fw-semibold mb-2'></label>}
                  {/* Uncomment to enable add/remove date rows
                  {index === inputField.leaveDate.length - 1 && (
                    <>
                      {index < 5 && (
                        <Button
                          type='button'
                          className='btn btn-light btn-light-primary me-2  btn-sm font-weight-bold'
                          style={{ height: '33px', width: '45px' }}
                          onClick={addLeaveDate}
                        >
                          <i className='fa-solid fa-plus'></i>
                        </Button>
                      )}
                      {index !== 0 && (
                        <Button
                          type='button'
                          className='btn btn-light btn-light-danger me-2  btn-sm font-weight-bold'
                          style={{ height: '33px', width: '45px' }}
                          onClick={() => removeLeaveDate(index)}
                        >
                          <i className='fa-solid fa-xmark'></i>
                        </Button>
                      )}
                    </>
                  )} */}
                </div>
              </div>
            </div>
          ))}

          {selectedReasonMeta?.isEmergency && (
            <div className='row ms-3'>
              <div className='fv-row me-6 col-md-4 col-6 mb-3'>
                <label className='form-label'>Attachment</label>
                <div className='text-info mb-2' style={{ fontSize: '12px' }}>
                  Supporting document is recommended for emergency leave and will be required before approval.
                </div>
                <CustomFileInput
                  name='attachment'
                  value={inputField.attachment}
                  onChange={(file: File | null) => {
                    setInputField((prev: any) => ({ ...prev, attachment: file }));
                  }}
                />
                {inputError?.attachment && (
                  <div className='text-danger' style={{ fontSize: '13px', marginTop: '5px' }}>
                    {inputError.attachment}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className='align-items-center border-bottom  min-h-150px'>
            {/* Show description field for Casual Leave, reason textarea for others */}
            {(() => {
              const selectedLeaveType = leaveType.find(lt => lt.value === inputField.leaveType);
              const isCasualLeave = selectedLeaveType?.label?.toLowerCase().includes('casual');
              return isCasualLeave;
            })() ? (
              <div className='px-8 py-4'>
                <div className='d-flex justify-content-between align-items-center mb-3'>
                  <label className='form-label required mb-0'>Description</label>
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => {
                      const startDate = inputField.leaveDate?.[0]?.startDate || '';
                      const endDate = inputField.leaveDate?.[inputField.leaveDate.length - 1]?.endDate || '';
                      const employeeName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim();
                      const template = generateLeaveTemplate(startDate, endDate, employeeName);

                      setInputField((prevInputField: any) => ({
                        ...prevInputField,
                        description: template,
                      }));
                    }}
                  >
                    Use Template
                  </Button>
                </div>
                <Textarea
                  value={inputField.description || ''}
                  onChange={(e) => {
                    setInputField((prevInputField: any) => ({
                      ...prevInputField,
                      description: e.target.value,
                    }));
                  }}
                  maxLength={1000}
                  minRows={12}
                  maxRows={18}
                  placeholder='Provide detailed description of your leave request or click "Use Template" for a standard format...'
                  style={{
                    minHeight: '250px',
                    fontSize: '14px',
                    lineHeight: '1.6'
                  }}
                  styles={{
                    input: {
                      padding: '12px',
                      borderRadius: '4px',
                    }
                  }}
                />
                {inputError?.description && (
                  <div className='text-danger' style={{ fontSize: '13px', marginTop: '5px' }}>
                    {inputError.description}
                  </div>
                )}
                <div className='d-flex justify-content-between align-items-center mt-2'>
                  <small className='text-muted' style={{ fontSize: '12px' }}>
                    {(inputField.description || '').length}/1000 characters
                  </small>
                  <small className='text-info' style={{ fontSize: '11px' }}>
                    💡 Tip: Include contact info and work delegation details
                  </small>
                </div>
              </div>
            ) : (
              // For other leave types, show traditional reason textarea with Use Template button
              <div className='px-8 py-4'>
                <div className='d-flex justify-content-between align-items-center mb-3'>
                  <label className='form-label required mb-0'>Reason</label>
                  <Button
                    size='sm'
                    variant='outline'
                    onClick={() => {
                      const startDate = inputField.leaveDate?.[0]?.startDate || '';
                      const endDate = inputField.leaveDate?.[inputField.leaveDate.length - 1]?.endDate || '';
                      const employeeName = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim();
                      const template = generateLeaveTemplate(startDate, endDate, employeeName);

                      setInputField((prevInputField: any) => ({
                        ...prevInputField,
                        reason: template,
                      }));
                    }}
                  >
                    Use Template
                  </Button>
                </div>
                {(() => {
                  const selectedLeaveType = leaveType.find(lt => lt.value === inputField.leaveType);
                  const isShortLeave = selectedLeaveType?.label?.toLowerCase().includes('short');
                  return isShortLeave && (
                    <div className='alert alert-info mb-3 p-3' style={{ fontSize: '12px' }}>
                      <strong>📝 Short Leave Note:</strong> Please specify exact start and end times in your reason (e.g., "Personal work from 2:00 PM to 4:00 PM")
                    </div>
                  );
                })()}
                <Textarea
                  value={inputField.reason}
                  onChange={(e) => {
                    setInputField((prevInputField: any) => ({
                      ...prevInputField,
                      reason: e.target.value,
                    }));
                  }}
                  minRows={12}
                  maxRows={18}
                  placeholder={(() => {
                    const selectedLeaveType = leaveType.find(lt => lt.value === inputField.leaveType);
                    const isShortLeave = selectedLeaveType?.label?.toLowerCase().includes('short');
                    return isShortLeave
                      ? 'Enter reason and specify exact times (e.g., "Personal work from 2:00 PM to 4:00 PM on [date]") or click "Use Template" for a standard format...'
                      : 'Enter reason or click "Use Template" for a standard format...';
                  })()}
                  style={{
                    minHeight: '250px',
                    fontSize: '14px',
                    lineHeight: '1.6'
                  }}
                  styles={{
                    input: {
                      padding: '12px',
                      borderRadius: '4px',
                    }
                  }}
                />
                <div className='d-flex justify-content-end mt-2'>
                  <small className='text-info' style={{ fontSize: '11px' }}>
                    💡 Tip: Provide clear and detailed reason for your leave request
                  </small>
                </div>
              </div>
            )}
            {/* Show error for reason field */}
            {inputError?.reason && (
              <div className='ms-7 text-danger' style={{ fontSize: '13px' }}>{inputError.reason}</div>
            )}
          </div>
          <div className='ms-7'>
            <InputErrorBox Message={inputError?.reason} />
          </div>
          <div className='d-flex flex-stack flex-wrap gap-2 py-5 ps-8 pe-5 border-top'>
            <div className='d-flex align-items-center me-3'>
              <Button
                //variant='primary' //It is not show to color so updated code
                size='sm'
                loading={SubmitAPIStatus.loading}
                onClick={onSubmit}
                style={{
                  backgroundColor: "#0d6efd",
                  borderColor: "#0d6efd",
                  color: "#fff",
                  padding: "6px 18px",
                  borderRadius: "6px"
                }}

              >
                Send
              </Button>
            </div>
            {/* <div className='d-flex align-items-center'>
              <span
                className='btn btn-icon btn-sm btn-light-danger btn-active-danger'
                data-inbox='dismiss'
                data-toggle='tooltip'
                title='Discard'
                onClick={onTrash}
              >
                <i className='fa-solid fa-trash'></i>
              </span>
            </div> */}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default MyLeaveModal